import Actions from "../actions";
import { ApplicationState, AppThunkAction, AppThunkDispatch } from "..";
import { 
    EntitiesLoadedAction,
    EntityAction,
    EntityErrorAction,
    EntitiesAction,
    EntitiesErrorAction,
    EntityDataAction,
    EntityChangeLogLoadedAction,
} from "./types";
import { alertError, alertSuccess } from "../alerts/actions";
import { TK } from "../translations/translationKeys";
import {
    EntityType,
    Entity,
    UserEntity,
    ClientEntity,
    ProfessionalEntity,
    AppointmentEntity,
    ReportEntity,
    ClientPackEntity,
    FileEntity,
    PaymentEntity,
    CompanyEntity,
    PrescriptionEntity,
    ClientOrderEntity,
} from "./state";
import { getNotifications } from "../general/selectors";
import { loadNotifications } from "../general/actions";
import { getEntitiesPageSize } from "../settings/selectors";
import { AppointmentData, BulkAppointmentCreationResult, ChangeLog, ClientData, FeatureEnum, PaymentOrigin, PaymentStatus, PrescriptionData, ProfessionalData, Report } from "../../models";
import { ClientReportsAndFiles, ReportTemplateData } from "../../models/api-models";
import { canSeeChangeLog, getMyProffesionalData as getMyProfessionalData, getMyUser, hasFeature } from "../session/selectors";
import { getAppointmentsForClient, getChangingValue, getEntitiesDataItems, getEntity } from "./selectors";
import { getAppointmentAsync, getClientAsync, getClientReportsAsync, getClientsAsync, getProfessionalAsync, getProfessionalsAsync, getPrescriptionsByClientAsync, createPrescriptionAsync } from "../../ajax/requests";
import { isBadRequestError, isCancelledError, isForbiddenError, isUnauthorizedError } from "../../ajax/errors";
import { getNowDateString, Guid } from "../../shared/utils";
import { resolvePlaceholders } from "../../shared/placeholders";
import { translate } from "../translations/hooks";
import { push } from "connected-react-router";
import { paths } from "../../app/constants";

const showSuccessMessages = false;

interface Defaults extends Record<EntityType, () => Entity> {
    users: () => UserEntity
    clients: () => ClientEntity
    professionals: () => ProfessionalEntity
    appointments: () => AppointmentEntity
    reports: () => ReportEntity
    clientPacks: () => ClientPackEntity
    files: () => FileEntity
    payments: () => PaymentEntity
    companies: () => CompanyEntity
    prescriptions: () => PrescriptionEntity
    orders: () => ClientOrderEntity
}

const defaultEntities: Defaults = {
    users: () => ({ 
        id: Guid.newGuid(),
        username: '', 
        permissions: [],
    }),
    clients: () => ({
        id: Guid.newGuid(),
        name: '',
    }),
    professionals: () => ({
        id: Guid.newGuid(),
        name: '',
    }),
    appointments: () => ({
        id: Guid.newGuid(),
        date: new Date().toISOString().substr(0, 10),
        start: "08:00",
        end: "09:00",
        clientId: undefined,
        expenses: [],
        payments: [],
        price: 0,
        professionalId: '',
        room: '',
        type: '',
    }),
    reports: () => ({
        id: Guid.newGuid(),
        creationDate: new Date().toISOString(),
        createdBy: '',
        clientId: '',
        name: '',
        templateId: '',
        values: {},
        version: 0,
    }),
    clientPacks: () => ({
        id: Guid.newGuid(),
        creationDate: new Date().toISOString(),
        appointmentTypes: [],
        clientId: '',
        clientName: '',
        createdBy: '',
        packName: '',
        packTemplateId: '',
        packVersion: 0,
        price: 0,
        totalCreditsStillAvailable: 0,
        totalMinutesStillAvailable: 0,
    }),
    files: () => ({
        id: Guid.newGuid(),
        creationDate: new Date().toISOString(),
        clientId: '',
        createdBy: '',
        fileName: '',
        url: '',
    }),
    payments: () => ({
        id: Guid.newGuid(),
        clientName: '',
        date: getNowDateString(),
        origin: PaymentOrigin.None,
        status: PaymentStatus.None,
        originId: '',
        type: '',
        value: 0,
    }),
    changeLogs: () => ({
        id: Guid.newGuid(),
    }),
    communications: () => ({
        id: Guid.newGuid(),
    }),
    companies: () => ({
        id: Guid.newGuid(),
        name: '',
        contacts: {},
    }),
    expenses: () => ({
        id: Guid.newGuid(),
    }),
    stock: () => ({
        id: Guid.newGuid(),
    }),
    prescriptions: () => ({
        id: Guid.newGuid(),
        clientId: '',
        state: 0,
        lines: [],
        creationDate: new Date().toISOString(),
        numeroReceitaLocal: '',
    }),
    orders: () => ({
        id: Guid.newGuid(),
        clientId: '',
        clientName: '',
        creationDate: new Date().toISOString(),
        createdBy: '',
        items: [],
        payments: [],
        vat: 0,
        missingToPay: 0,
    }),
}

const entitiesLoading = (entityType: EntityType): EntitiesAction => ({
    type: Actions.EntitiesLoading,
    entityType,
});

export const entitiesLoaded = (
    entityType: EntityType, 
    entities: Entity[], 
    entitiesToRemove: string[], 
    loadType: 'NewResult' | 'AppendResult' | 'ReducedDataLoad',
): EntitiesLoadedAction => ({
    type: Actions.EntitiesLoaded,
    entityType,
    entities,
    entitiesToRemove,
    loadType,
}); 

export const entitiesLoadClear = (entityType: EntityType): EntitiesAction => ({
    type: Actions.EntitiesSearchReset,
    entityType,
});

export const updateCommunicationStatus = (
    id: string,
    status: number,
    statusUpdateTimestamp: string
): AppThunkAction<void> => {
    return (dispatch, getState) => {
        const state = getState();
        const existingEntity = state.entities.communications.items[id];

        if (existingEntity) {
            const updatedEntity = {
                ...existingEntity,
                status,
                statusUpdateTimestamp,
            };
            dispatch(entitiesLoaded('communications', [updatedEntity], [], 'AppendResult'));
        }
    };
};

const entityDeleted = (entityType: EntityType, id: string): EntityAction => ({
    type: Actions.EntityDeleted,
    entityType,
    entityId: id,
});

export const entityCreating = <T extends Entity = Entity>(entityType: EntityType, initialValue?: T): EntityDataAction => ({
    type: Actions.EntityCreating,
    entityType,
    entity: {
        ...defaultEntities[entityType](),
        ...(initialValue ?? {}),
    },
    keepItOpen: true,
});

export const entityEditing = (entityType: EntityType, id: string, additionalActions?: ('edit-payment' | 'create-invoice')[]): EntityAction => ({
    type: Actions.EntityEditing,
    entityType,
    entityId: id,
    additionalActions,
});

const entityEditionSubmitting = (entityType: EntityType, id: string): EntityAction => ({
    type: Actions.EntityEditionSubmitting,
    entityType,
    entityId: id,
});

const entityEditionSubmitted = (entityType: EntityType, entity: Entity, keepItOpen = false): EntityDataAction => ({
    type: Actions.EntityEditionSubmitted,
    entityType,
    entity,
    keepItOpen,
});

const entityEditionSubmitError = (entityType: EntityType, id: string, error: string): EntityErrorAction => ({
    type: Actions.EntityEditionSubmitError,
    entityType,
    entityId: id,
    error,
});

export const entityEditionCancel = (entityType: EntityType): EntitiesAction => ({
    type: Actions.EntityEditionCancel,
    entityType,
});

export const entityCreatingCancel = (entityType: EntityType): EntitiesAction => ({
    type: Actions.EntityCreatingCancel,
    entityType,
});

const newEntitySubmitting = (entityType: EntityType): EntitiesAction => ({
    type: Actions.EntityNewSubmitting,
    entityType,
});

const newEntitySubmitError = (entityType: EntityType, error: string): EntitiesErrorAction => ({
    type: Actions.EntityNewSubmitError,
    entityType,
    error,
});

const newEntitySubmitted = (entityType: EntityType, entity: Entity, keepItOpen: boolean = false): EntityDataAction => ({
    type: Actions.EntityNewSubmitted,
    entityType,
    entity,
    keepItOpen,
});

const entityChangeLogLoading = (entityType: EntityType, entityId: string): EntityAction => ({
    type: Actions.EntityChangeLogLoading,
    entityType,
    entityId,
});

const entityChangeLogLoaded = (entityType: EntityType, entityId: string, changeLog: ChangeLog[]): EntityChangeLogLoadedAction => ({
    type: Actions.EntityChangeLogLoaded,
    entityType,
    entityId,
    changeLog,
});

export const updateChangingEntity = (entityType: EntityType, entity: Entity, keepItOpen = false): EntityDataAction => ({
    type: Actions.EntityChangingUpdate,
    entityType,
    entity,
    keepItOpen,
});

export const entityOpened = (entityType: EntityType, id: string): EntityAction => ({
    type: Actions.EntityOpened,
    entityType,
    entityId: id,
});

export const openEntity = <T extends Entity>(entityType: EntityType, entityId: string, getRequest: () => Promise<T>): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const found = !!getEntity(getState(), entityType, entityId)
        if (found) dispatch(entityEditing(entityType, entityId))
        const [entity] = await dispatch(loadEntities(entityType, getRequest, 'AppendResult'))
        if (!found && entity) dispatch(entityEditing(entityType, entityId))
    }
}

export const openClient = (clientId: string) : AppThunkAction<Promise<void>> => {
    return async (dispatch) => {
        await dispatch(openEntity('clients', clientId, () => getClientAsync(clientId)));
    }
}

export const openAppointment = (appointmentId: string, additionalActions?: ('edit-payment' | 'create-invoice')[]): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const found = !!getEntity(getState(), 'appointments', appointmentId)
        if (found) dispatch(entityEditing('appointments', appointmentId, additionalActions))
        const [entity] = await dispatch(loadEntities('appointments', () => getAppointmentAsync(appointmentId), 'AppendResult'))
        if (!found && entity) dispatch(entityEditing('appointments', appointmentId, additionalActions))
    }
}

export const openClientPage = (clientId: string) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const entity = getEntity<ClientEntity>(getState(), 'clients', clientId);
        dispatch(loadEntities('clients', (pageSize) => getClientsAsync(`"${entity?.name}"`, pageSize), 'NewResult'));
        dispatch(push({pathname: paths.clients(), search: `?id=${clientId}`}));
    }
}

export const openProfessional = (
    professionalId: string,
    tab?: string,
) : AppThunkAction<Promise<void>> => {
    return async (dispatch) => {
        await dispatch(loadEntities('professionals', () => getProfessionalAsync(professionalId), 'AppendResult'));
        dispatch(push({pathname: paths.professionals(), search: tab ? `?id=${professionalId}&tab=${encodeURIComponent(tab)}` : `?id=${professionalId}`}));
    }
}

export const loadEntities = <T extends Entity>(
    entityType: EntityType, 
    getRequest: ((pageSize: number) => Promise<T[]>)|(() => Promise<T>), 
    loadType: 'NewResult' | 'AppendResult' | 'ReducedDataLoad',
    entitiesToReplace?: string[] | ((e: T) => boolean),
) : AppThunkAction<Promise<Entity[]>> => {
    return async (dispatch, getState) => {
        const entitiesToRemove = !entitiesToReplace ? []
            : Array.isArray(entitiesToReplace) ? entitiesToReplace 
            : getEntitiesDataItems<T>(entityType)(getState()).filter(entitiesToReplace).map(e => e.id)

        try {
            const state = getState();
            
            dispatch(entitiesLoading(entityType));
            const result = await getRequest(getEntitiesPageSize(state));
            const entities = Array.isArray(result) ? result : result ? [result] : []
            dispatch(entitiesLoaded(entityType, entities, entitiesToRemove, loadType));

            // Removed to avoid enter in edition mode - but not sure if this is needed
            // so, if this is still here after 1 month, just remove this comment and the code inside
            // if (entities.length === 1 && entities[0].id === getChangingValue(state, entityType)?.id) {
            //     dispatch(updateChangingEntity(entityType, entities[0], true))
            // }

            return entities;
        }
        catch(error) {
            console.error(error, 'Error loading entities');
            if(isUnauthorizedError(error) || isCancelledError(error)) return []
            dispatch(entitiesLoaded(entityType, [], entitiesToRemove, loadType));
            dispatch(showError(error));
        }
        return [];
    };
};

export const deleteOpenEntity = <T extends Entity>(entityType: EntityType, deleteRequest: (entity: T) => Promise<void>): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const entity = getChangingValue(getState(), entityType) as T
            if(entity?.id) {
                dispatch(deleteEntity(entityType, entity.id, () => deleteRequest(entity)))
            }
        }
        catch(error) {
            console.error(error)
            if(isUnauthorizedError(error) || isCancelledError(error)) return
            dispatch(showError(error));
        }
    }
}

export const deleteEntity = (
    entityType: EntityType, 
    id: string, 
    deleteRequest: (id: string) => Promise<void>,
    entitiesToRemove?: string[] | ((state: ApplicationState) => string[]),
) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            await deleteRequest(id);
            const extraEntitiesToRemove = typeof entitiesToRemove === 'function' 
                ? entitiesToRemove(getState()) 
                : entitiesToRemove ?? [];
            const idsToRemove = [...new Set([id, ...extraEntitiesToRemove.filter((value): value is string => !!value)])];

            dispatch(entityDeleted(entityType, id));
            if(idsToRemove.length) {
                dispatch(entitiesLoaded(entityType, [], idsToRemove, 'AppendResult'));
            }
            if(showSuccessMessages) dispatch(alertSuccess(translate(TK.entityDeletedSuccessfully)));
        }
        catch(error) {
            console.error(error)
            if(isUnauthorizedError(error) || isCancelledError(error)) return
            dispatch(showError(error));
        }
    };
};

export const updateEntity = <T extends Entity>(entityType: EntityType, newData: T, updateRequest: (data: T) => Promise<void>, keepItOpen = false) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            newData = {...newData, lastChangeBy: getMyUser(state)?.username, lastChangeDt: new Date().toISOString()}

            dispatch(entityEditionSubmitting(entityType, newData.id));

            await updateRequest(newData);

            dispatch(entityEditionSubmitted(entityType, newData, keepItOpen));
            if(showSuccessMessages) dispatch(alertSuccess(translate(TK.entityChangedSuccessfully)));

            if(getNotifications(state).some(n => n.entityId === newData.id)){
                dispatch(loadNotifications());
            }
        }
        catch(error) {
            console.error(error)
            if(isUnauthorizedError(error) || isCancelledError(error)) return
            dispatch(entityEditionSubmitError(entityType, newData.id, getErrorMessage(getState, error)));
            dispatch(showError(error));
            throw error; // Re-throw to prevent form from closing on error
        }
    };
};

export const createEntity = <T extends Entity>(entityType: EntityType, data: T, createRequest: (data: T, state: ApplicationState) => Promise<string | (Partial<T> & { id: string })>) : AppThunkAction<Promise<string | undefined>> => {
    return async (dispatch, getState) => {
        try {
            dispatch(newEntitySubmitting(entityType));

            const result = await createRequest(data, getState());
            const [id, extra]: [string, Partial<T>] = typeof result === 'string'
                ? [result, {}]
                : [result.id, result];

            dispatch(newEntitySubmitted(entityType, {...data, ...extra, id}));
            if(showSuccessMessages) dispatch(alertSuccess(translate(TK.entityCreatedSuccessfully)));

            return id;
        }
        catch(error) {
            console.error(error)
            if(isUnauthorizedError(error) || isCancelledError(error)) return undefined

            dispatch(newEntitySubmitError(entityType, getErrorMessage(getState, error)));
            dispatch(showError(error));

            return undefined;
        }
    };
};

export const createManyAppointments = (data: AppointmentEntity[], createRequest: (data: AppointmentEntity[]) => Promise<BulkAppointmentCreationResult>) : AppThunkAction<Promise<BulkAppointmentCreationResult|null>> => {
    const entityType: EntityType = 'appointments'

    return async (dispatch, getState) => {
        try {
            const state = getState();

            dispatch(newEntitySubmitting(entityType));
            
            const res = await createRequest(data);
            const {results} = res

            const errorMessages = results.map(r => r.errorMessage).filter((msg: string|undefined): msg is string => !!msg)
            const keepEditing = !!errorMessages.length

            for (const {id, date, start} of results) {
                if (id) {
                    const newItem = data.find(a => a.date === date && a.start === start)
                    dispatch(newEntitySubmitted(entityType, {...newItem, id}, keepEditing));
                }
            }

            if (errorMessages.length) errorMessages.forEach(msg => dispatch(alertError(msg))) 
            else if (showSuccessMessages) dispatch(alertSuccess(translate(TK.entityCreatedSuccessfully)));

            return res;
        }
        catch(error) {
            console.error(error)
            if(isUnauthorizedError(error) || isCancelledError(error)) return null

            dispatch(newEntitySubmitError(entityType, getErrorMessage(getState, error)));
            dispatch(showError(error));

            return null;
        }
    };
};

export const loadOpenEntityChangeLog = <T extends Entity>(entityType: EntityType, getRequest: (entity: T) => Promise<ChangeLog[]>): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const entity = getChangingValue(getState(), entityType) as T
            if(entity?.id) {
                dispatch(loadEntityChangeLog(entityType, entity.id, () => getRequest(entity)))
            }
        }
        catch(error) {
            console.error(error)
            if(isUnauthorizedError(error) || isCancelledError(error)) return
            dispatch(showError(error));
        }
    }
}

export const loadEntityChangeLog = (entityType: EntityType, entityId: string, getRequest: () => Promise<ChangeLog[]>) : AppThunkAction<Promise<ChangeLog[]>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            if(!canSeeChangeLog(state)) {
                return []
            }

            dispatch(entityChangeLogLoading(entityType, entityId));
            const changeLog = await getRequest();
            dispatch(entityChangeLogLoaded(entityType, entityId, changeLog));
            return changeLog;
        }
        catch(error) {
            console.error(error)
            dispatch(entityChangeLogLoaded(entityType, entityId, []));
            dispatch(showError(error));
        }
        return [];
    };
};

export const serverCall = (call: (state: ApplicationState) => Promise<unknown>, successMessage: TK) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState()
            await call(state)
            dispatch(alertSuccess(translate(successMessage)))
        }
        catch(error) {
            console.error(error)
            dispatch(showError(error));
        }
    };
};


export const fetchClientReportsAndFiles = (clientId: string): AppThunkAction<Promise<ClientReportsAndFiles>> => {
    return async (dispatch, getState) => {
        const state = getState()
        const result = await getClientReportsAsync(clientId, getEntitiesPageSize(state), 0)
        dispatch(loadEntities('reports', async () => result.reports, 'NewResult'))
        dispatch(loadEntities('files', async () => result.files, 'NewResult'))
        return result
    }
}

const populateDefaultValue = (text: string, client: ClientData, appointment?: AppointmentData, professional?: ProfessionalData) =>
    resolvePlaceholders(text, {
        clientName: client.name,
        clientIdentificationNumber: client.identificationNumber,
        clientTaxNumber: client.taxNumber,
        clientHealthNumber: client.healthNumber,
        clientSubsystemName: client.subsystemName,
        clientSubsystemNumber: client.subsystemNumber,
        clientBirthday: client.birthday,
        clientPhoneNumber: client.phoneNumbers?.[0],
        clientEmail: client.email,
        clientFileNumber: client.clientFileNumber?.toString(),
        clientProperties: client.properties,
        appointmentDate: appointment?.date,
        appointmentStart: appointment?.start,
        appointmentEnd: appointment?.end,
        appointmentType: appointment?.type,
        appointmentRoom: appointment?.room,
        date: appointment?.date,
        time: appointment?.start,
        professionalName: professional?.name ?? appointment?.professionalName,
        professionalRegistrationNumber: professional?.registrationNumber,
        professionalEmail: professional?.email,
        professionalPhoneNumber: professional?.phoneNumbers?.[0],
    })

export const fetchPrescriptionsByClient = (clientId: string): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        if (!hasFeature(FeatureEnum.Prescriptions)(getState())) {
            return;
        }
        try {
            const prescriptions = await getPrescriptionsByClientAsync(clientId);
            dispatch(entitiesLoaded('prescriptions', prescriptions, [], 'AppendResult'));
        } catch(error) {
            console.error(error, 'Error loading prescriptions');
        }
    };
};

export interface CreatePrescriptionCommand {
    clientId: string;
    professionalId: string;
    tipoReceita: string;
    renovavel: boolean;
    lines: {
        cnPem: string;
        substanciaActiva: string;
        formaFarmaceutica: string;
        embalagem: string;
        dosagem: string;
        quantidade: number;
        instrucoes?: string;
    }[];
}

export const createPrescription = (command: CreatePrescriptionCommand): AppThunkAction<Promise<PrescriptionData | undefined>> => {
    return async (dispatch, getState) => {
        try {
            const prescription = await createPrescriptionAsync(command);
            dispatch(entitiesLoaded('prescriptions', [prescription], [], 'AppendResult'));
            return prescription;
        } catch(error) {
            dispatch(alertError(getErrorMessage(getState, error)));
            return undefined;
        }
    };
};

export const createReport = (
    reportTemplate: ReportTemplateData,
    client: ClientData,
    appointment?: AppointmentData,
): AppThunkAction<void> => {
    return (dispatch, getState) => {
        const state = getState()
        const professional = getMyProfessionalData(state)
        if(!appointment) {
            const allClientAppointments = getAppointmentsForClient(client.id)(state);
            const nowDateString = getNowDateString();
            // Prefer the most recent past appointment; fall back to nearest future appointment
            const pastAppointments = allClientAppointments
                .filter(a => a.date <= nowDateString)
                .sort((a, b) => a.date > b.date ? -1 : 1);
            if (pastAppointments.length > 0) {
                appointment = pastAppointments[0];
            } else {
                appointment = allClientAppointments
                    .filter(a => a.date > nowDateString)
                    .sort((a, b) => a.date < b.date ? -1 : 1)[0];
            }
        }
        const report: Report = {
            id: '',
            creationDate: new Date().toISOString(),
            createdBy: getMyUser(state)?.username ?? 'unknown',
            templateId: reportTemplate.templateId,
            version: reportTemplate.version,
            name: reportTemplate.name,
            clientId: client.id,
            clientName: client.name,
            professionalId: professional?.id,
            professionalName: professional?.name,
            visibleForAdministratives: reportTemplate.availableForAppointmentsManager,
            values: Object.fromEntries(reportTemplate.fields
                .filter(f => f.defaultValue)
                .map(f => [f.label, populateDefaultValue(f.defaultValue!, client, appointment, professional)])),
        }
        dispatch(entityCreating('reports', report))
    }
}

export const copyOpenReport = (values: Record<string, string>): AppThunkAction<void> => {
    return (dispatch, getState) => {
        const state = getState()
        
        const report = getChangingValue(state, 'reports') as ReportEntity|undefined
        if(!report) {
            dispatch(alertError(translate(TK.somethingWentWrong)))
            console.warn('No report open to be copied')
            return
        }
        
        const user = getMyUser(state)
        const professional = getMyProfessionalData(state)
        if (!user || (report.professionalId && !professional)) {
            dispatch(alertError(translate(TK.youReNotAllowedToCreateThisReport)))
            return
        }

        dispatch(entityCreating('reports', {
            ...report, 
            id: '', 
            values,
            creationDate: new Date().toISOString(),
            createdBy: user.username,
            professionalId: professional?.id,
            professionalName: professional?.name,
        }))
    }
}

export const showError = (error: unknown): AppThunkAction<void> => {
    return (dispatch, getState) => {
        if(!error) return // it's already handled in requests.prod.ts
        dispatch(alertError(getErrorMessage(getState, error)))
    }
}

const getErrorMessage = (getState: () => ApplicationState, error: unknown): string => {
    const data = (error as { data?: any })?.data;
    if (data) {
        // Prefer explicit message
        if (typeof data.message === 'string' && data.message.trim().length) return data.message;
        // Try standard ASP.NET Core validation errors shape
        const errors = data.errors as Record<string, string[] | string> | undefined;
        if (errors && typeof errors === 'object') {
            const messages = Object.values(errors)
                .flatMap(v => Array.isArray(v) ? v : [v])
                .filter(Boolean) as string[];
            if (messages.length) return messages.join('\n');
        }
        // Fallback to title if present (ProblemDetails)
        if (typeof data.title === 'string' && data.title.trim().length) return data.title;
    }
    return isBadRequestError(error) ? translate(TK.badRequestPleaseConfirmDataSent)
        : isForbiddenError(error) ? translate(TK.notAllowedToPerformThisOperation)
        : isUnauthorizedError(error) ? translate(TK.notAllowedToPerformThisOperation)
        : translate(TK.somethingWentWrong);
}
